import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, User, ChevronRight, ChevronLeft } from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { DynamicSeoHead } from '../components/DynamicSeoHead';
import { supabase } from '../lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  published_at: string;
  featured_image_id: string | null;
  featured_image_url?: string;
  featured_image_alt?: string;
  author_name: string;
  categories: Array<{ id: string; name: string; slug: string }>;
}

const POSTS_PER_PAGE = 10;

export function BlogListPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<Array<{ id: string; name: string; slug: string }>>([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [currentPage, searchQuery, selectedCategory]);

  async function fetchCategories() {
    const { data } = await supabase
      .from('blog_categories')
      .select('id, name, slug')
      .order('name');

    if (data) {
      setCategories(data);
    }
  }

  async function fetchPosts() {
    setLoading(true);

    let query = supabase
      .from('blog_posts')
      .select(`
        id,
        title,
        slug,
        excerpt,
        published_at,
        featured_image_id,
        author_id,
        user_profiles!blog_posts_author_id_fkey (
          first_name,
          last_name
        )
      `, { count: 'exact' })
      .eq('status', 'published')
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false });

    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,excerpt.ilike.%${searchQuery}%`);
    }

    if (selectedCategory) {
      const { data: postIds } = await supabase
        .from('blog_post_categories')
        .select('post_id')
        .eq('category_id', selectedCategory);

      if (postIds && postIds.length > 0) {
        query = query.in('id', postIds.map(p => p.post_id));
      } else {
        setPosts([]);
        setTotalPages(0);
        setLoading(false);
        return;
      }
    }

    const from = (currentPage - 1) * POSTS_PER_PAGE;
    const to = from + POSTS_PER_PAGE - 1;

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error('Error fetching posts:', error);
      setLoading(false);
      return;
    }

    if (data) {
      const postsWithImages = await Promise.all(
        data.map(async (post: any) => {
          let featuredImageUrl = '';
          let featuredImageAlt = '';

          if (post.featured_image_id) {
            const { data: media } = await supabase
              .from('media')
              .select('url, alt')
              .eq('id', post.featured_image_id)
              .maybeSingle();

            if (media) {
              featuredImageUrl = media.url;
              featuredImageAlt = media.alt || '';
            }
          }

          const { data: postCategories } = await supabase
            .from('blog_post_categories')
            .select('category_id, blog_categories (id, name, slug)')
            .eq('post_id', post.id);

          const categories = postCategories?.map((pc: any) => ({
            id: pc.blog_categories.id,
            name: pc.blog_categories.name,
            slug: pc.blog_categories.slug
          })) || [];

          return {
            id: post.id,
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt,
            published_at: post.published_at,
            featured_image_id: post.featured_image_id,
            featured_image_url: featuredImageUrl,
            featured_image_alt: featuredImageAlt,
            author_name: post.user_profiles
              ? `${post.user_profiles.first_name || ''} ${post.user_profiles.last_name || ''}`.trim()
              : 'Trezoone',
            categories
          };
        })
      );

      setPosts(postsWithImages);
      setTotalPages(Math.ceil((count || 0) / POSTS_PER_PAGE));
    }

    setLoading(false);
  }

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://trezoone.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://trezoone.fr/blog"
      }
    ]
  };

  return (
    <>
      <DynamicSeoHead
        slug="/blog"
        fallback={{
          title: "Blog Trezoone – Conseils et guides sur la gestion de trésorerie",
          description: "Découvrez nos articles sur le pilotage financier, la gestion de trésorerie et les meilleures pratiques pour TPE, PME et associations.",
          keywords: "blog gestion trésorerie, conseils pilotage financier, guides TPE PME, articles comptabilité",
          jsonLd: [breadcrumbJsonLd]
        }}
      />

      <div className="min-h-screen bg-gray-50">
        <MarketingHeader
          onNavigate={(path) => navigate(path)}
          onSignIn={() => navigate('/login')}
          onSignUp={() => navigate('/login')}
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
            <button onClick={() => navigate('/')} className="hover:text-blue-600">
              Accueil
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">Blog</span>
          </nav>

          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Blog Trezoone
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Conseils pratiques, guides et actualités sur la gestion de trésorerie et le pilotage financier
            </p>
          </div>

          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">Aucun article trouvé</p>
            </div>
          ) : (
            <>
              <div className="grid gap-8 mb-12">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <div className="md:flex">
                      {post.featured_image_url && (
                        <div className="md:w-1/3 bg-gray-200">
                          <img
                            src={post.featured_image_url}
                            alt={post.featured_image_alt}
                            className="w-full h-64 md:h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className={`p-6 ${post.featured_image_url ? 'md:w-2/3' : 'w-full'}`}>
                        <div className="flex items-center gap-3 mb-3">
                          {post.categories.map((cat) => (
                            <span
                              key={cat.id}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {cat.name}
                            </span>
                          ))}
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                          {post.title}
                        </h2>

                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>

                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{post.author_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(post.published_at).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Précédent
                  </button>

                  <span className="text-gray-600">
                    Page {currentPage} sur {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </main>

        <MarketingFooter />
      </div>
    </>
  );
}
